import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { SubscriptionPriceService } from '@/services/subscriptionPriceService';
import { SubscriptionPrice, CreateSubscriptionPriceData, UpdateSubscriptionPriceData } from '@/types/subscriptionTypes';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Loader2, Plus, Edit, Archive } from 'lucide-react';
import { CreateSubscriptionPriceModal } from './CreateSubscriptionPriceModal';
import { UpdateSubscriptionPriceModal } from './UpdateSubscriptionPriceModal';

interface SubscriptionPriceManagerProps {
  organizationId: string;
  onError?: (error: any) => void;
}

export const SubscriptionPriceManager: React.FC<SubscriptionPriceManagerProps> = ({ organizationId, onError }) => {
  const queryClient = useQueryClient();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [selectedPrice, setSelectedPrice] = useState<SubscriptionPrice | null>(null);

  // Fetch subscription prices
  const { data: prices, isLoading, error: queryError } = useQuery({
    queryKey: ['subscriptionPrices', organizationId],
    queryFn: () => SubscriptionPriceService.getPricesForOrganization(organizationId)
  });

  // Handle query error
  React.useEffect(() => {
    if (queryError) {
      if (onError) onError(queryError);
      toast.error('Failed to load subscription prices');
      console.error('Load prices error:', queryError);
    }
  }, [queryError, onError]);

  // Create price mutation
  const createPriceMutation = useMutation({
    mutationFn: SubscriptionPriceService.createPrice,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscriptionPrices', organizationId] });
      toast.success('Subscription price created successfully');
      setIsCreateModalOpen(false);
    },
    onError: (error) => {
      if (onError) onError(error);
      toast.error('Failed to create subscription price');
      console.error('Create price error:', error);
    }
  });

  // Update price mutation
  const updatePriceMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateSubscriptionPriceData }) => 
      SubscriptionPriceService.updatePrice(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscriptionPrices', organizationId] });
      toast.success('Subscription price updated successfully');
      setIsUpdateModalOpen(false);
      setSelectedPrice(null);
    },
    onError: (error) => {
      if (onError) onError(error);
      toast.error('Failed to update subscription price');
      console.error('Update price error:', error);
    }
  });

  // Archive price mutation
  const archivePriceMutation = useMutation({
    mutationFn: SubscriptionPriceService.archivePrice,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscriptionPrices', organizationId] });
      toast.success('Subscription price archived successfully');
    },
    onError: (error) => {
      if (onError) onError(error);
      toast.error('Failed to archive subscription price');
      console.error('Archive price error:', error);
    }
  });

  const handleCreatePrice = (data: CreateSubscriptionPriceData) => {
    createPriceMutation.mutate(data);
  };

  const handleUpdatePrice = (id: string, data: UpdateSubscriptionPriceData) => {
    updatePriceMutation.mutate({ id, data });
  };

  const handleEditClick = (price: SubscriptionPrice) => {
    setSelectedPrice(price);
    setIsUpdateModalOpen(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 data-testid="loading-spinner" className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Subscription Prices</h2>
        <Button onClick={() => setIsCreateModalOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Add Price
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {prices?.map((price: SubscriptionPrice) => (
          <Card key={price.id}>
            <CardHeader>
              <CardTitle>{price.name}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <p className="text-2xl font-bold">
                  ${(price.amount_cents / 100).toFixed(2)}
                  <span className="text-sm text-gray-500">
                    /{price.billing_period}
                  </span>
                </p>
                {price.description && (
                  <p className="text-sm text-gray-500">{price.description}</p>
                )}
                <div className="pt-4 flex space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEditClick(price)}
                    disabled={updatePriceMutation.isPending}
                  >
                    {updatePriceMutation.isPending && selectedPrice?.id === price.id ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Edit className="h-4 w-4 mr-2" />
                    )}
                    Edit
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => archivePriceMutation.mutate(price.id)}
                    disabled={archivePriceMutation.isPending}
                  >
                    {archivePriceMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Archive className="h-4 w-4 mr-2" />
                    )}
                    Archive
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <CreateSubscriptionPriceModal
        organizationId={organizationId}
        onSubmit={handleCreatePrice}
        isOpen={isCreateModalOpen}
        onOpenChange={setIsCreateModalOpen}
      />

      {selectedPrice && (
        <UpdateSubscriptionPriceModal
          price={selectedPrice}
          onSubmit={handleUpdatePrice}
          isOpen={isUpdateModalOpen}
          onOpenChange={setIsUpdateModalOpen}
        />
      )}
    </div>
  );
}; 